'use client'

import ABTest from '@component/abTest'
import Avatar from '@component/avatar'
import Cover from '@component/cover'
import DisablePrefetchLink from '@component/disablePrefetchLink'
import houseSearchPageFrom from '@pageFrom/house'
import { favouriteDataLayer } from '@plugin/google/gtm/modules/property'
import { houseDetail } from '@router'
import { global } from '@store/index'
import { getColliersClassName, getTime } from '@utils'
import { getComingSoonDateLabel, isSoldData, isVillageData } from '@utils/house'
import classNames from 'classnames'
import { concat, first, isFunction, join, pick, take } from 'lodash-es'
import { Suspense, useCallback } from 'react'
import { useRecoilValue } from 'recoil'
import CoverSlide from '../coverSlide'
import HouseFacility from '../facility'
import { Favourite } from '../favourite'
import OfficeLine from '../office/line'
import Tags from '../tags'

export default function ListingSearchList({
  data,
  className,
  onMouseEnter,
  onMouseLeave,
  onClick,
  hoverTranslate = false,
  nativeLink = false,
  isPageAlt = false
}) {

  const { mobile } = useRecoilValue(global)
  const cover = first(concat(data.images, data.floorPlanImages))
  const agent = pick(first(data.agents) || {}, ['icon', 'name'])
  const street = join(concat(data.street, data.suburbName || []), ', ')
  const isVillage = isVillageData(data)
  const isSold = isSoldData(data)
  const comingSoonLabel = getComingSoonDateLabel(data, 'On Market {date}')

  const clickHandle = useCallback(event => {
    isFunction(onClick) && onClick(data, event)
    houseSearchPageFrom.serpListingClick(data)
  }, [data, onClick])
  const mouseEnterHandle = useCallback(event => isFunction(onMouseEnter) && onMouseEnter(data, event), [data, onMouseEnter])
  const mouseLeaveHandle = useCallback(event => isFunction(onMouseLeave) && onMouseLeave(data, event), [data, onMouseLeave])
  const Wrap = nativeLink ? 'a' : DisablePrefetchLink
  return (
    <Wrap
      href={houseDetail(data)}
      onMouseEnter={mouseEnterHandle}
      onMouseLeave={mouseLeaveHandle}
      onClick={clickHandle}
      className={classNames('flex flex-col justify-between rounded-sm overflow-hidden group shadow-md', { 'transition-transform duration-200 hover:-translate-x-2 hover:-translate-y-2': hoverTranslate }, className)}
      aria-label='go to property'
      target={mobile ? '_self' : '_blank'}
    >
      <div className='relative'>
        <ABTest
          newComponent={
            <CoverSlide
              data={data}
              images={take(data.images, 6)}
              width={368}
              height={284}
              className='aspect-[100/77]'
              imgClassName='object-cover block aspect-auto'
              alt={data.street}
            >
              <CoverMask data={data} />
            </CoverSlide>
          }
        >
          <CoverMask data={data} />
          <Cover src={cover} alt={`${data.street}_0`} hoverScale className='aspect-[100/77]' width={368} height={284} defaultIcon='icon-house-cover'></Cover>
        </ABTest>
        {/* <div className='absolute z-[1] left-0 top-0 p-10 w-full pointer-events-none'>
          <div className='flex items-start justify-between pointer-events-auto'>
            <Tags data={data}></Tags>
            {
              !isSold && <Favourite data={data} onClick={favouriteDataLayer.iconClick}></Favourite>
            }
          </div>
        </div>
        <Cover src={cover} alt={`${data.street}_0`} hoverScale className='aspect-[100/77]' width={368} height={284} defaultIcon='icon-house-cover'></Cover> */}
      </div>
      <div className='px-10 py-13 flex items-center relative'>
        <div className='flex-1 min-w-0'>
          <div className='text-sm text-font line-clamp-1'>
            {
              comingSoonLabel
                ? comingSoonLabel
                : (
                  isVillage
                    ? data.teaser
                    : [data.priceBold, data.priceLight].join(' ')
                )
            }
          </div>
          <address className={classNames('text-font not-italic line-clamp-1 mt-4', isPageAlt ? 'text-base' : 'text-sm')}>
            <i className={classNames('icon icon-address text-font align-middle mr-6 sm:hidden', { '!text-lg inline-block -mt-4': isPageAlt })}></i>{street}
          </address>
          <div className='mt-8 min-h-[21px]'>
            <HouseFacility data={data}></HouseFacility>
          </div>
        </div>
        <div className={classNames('ml-6', { 'absolute right-10 -bottom-25': isPageAlt })}>
          {
            (data.isRecommended || (isSold && data.agentPromoteSoldSearch)) && !isVillage && agent && (
              <Avatar src={agent.icon} circle alt={agent.name} width={60} height={60} defaultIcon='icon-avatar' className={getColliersClassName(data.office)}></Avatar>
            )
          }
        </div>
      </div>
      <OfficeLine data={data}></OfficeLine>
    </Wrap>
  )
}

export function SoldStandard({
  data,
  clickable = false
}) {

  const cover = first(concat(data.images, data.floorPlanImages))
  const street = join(concat(data.street, data.suburbName || []), ', ')

  const isLink = data.slug && clickable
  const BoxTag = isLink ? DisablePrefetchLink : 'div'

  return (
    <BoxTag href={isLink && houseDetail(data)} className='block rounded-sm overflow-hidden group shadow-md' aria-label='go to property'>
      <div className='relative'>
        {
          data.soldInfo && (
            <div className='absolute z-[1] left-0 top-0 p-10 w-full h-full pointer-events-none flex flex-col justify-between items-start bg-image-mask-sm'>
              <div className='bg-red text-white text-xs rounded-sm px-6 py-2'>Sold on {getTime(data.soldInfo.soldDate, 'DD MMM YYYY')}</div>
              <div className='text-white text-sm'>{join([data.priceBold, data.priceLight], ' ')}</div>
            </div>
          )
        }
        <Cover src={cover} alt={data.teaser} hoverScale className='aspect-[267/140]' width={267} height={140} defaultIcon='icon-house-cover'></Cover>
      </div>
      <div className='px-10 py-12 flex items-center'>
        <div className='flex-1 min-w-0'>
          <div className='text-font text-xs line-clamp-1 mt-4'>
            <i className='icon icon-address text-font align-middle mr-6'></i>{street}
          </div>
          <div className='mt-8 min-h-[21px]'>
            <HouseFacility data={data}></HouseFacility>
          </div>
        </div>
      </div>
    </BoxTag>
  )
}

function CoverMask({ data }) {
  const isSold = isSoldData(data)

  return (
    <div className='absolute z-[3] left-0 top-0 p-10 w-full pointer-events-none'>
      <div className='flex items-start justify-between pointer-events-auto'>
        <Tags data={data}></Tags>
        {
          !isSold && (
            <Suspense>
              <Favourite data={data} onClick={favouriteDataLayer.iconClick}></Favourite>
            </Suspense>
          )
        }
      </div>
    </div>
  )
}

export function SkeletonCard() {
  return (
    <div className='skeleton-wrap space-y-10'>
      <span style={{ aspectRatio: 267 / 140 }}></span>
      <div className='flex items-center'>
        <div className='flex-1 mr-10 space-y-10'>
          <span className='h-10'></span>
          <span className='h-10 w-3/4'></span>
        </div>
        <span className='w-50 h-50 !rounded-half'></span>
      </div>
    </div>
  )
}